import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"

// Drag-to-reorder for the APP items in the nav rail / bottom dock.
//
// Deliberately its own controller rather than a generalised pin_reorder: the
// arrange-your-home editor drags <div> cards on two axes inside a page, this
// drags <a> LINKS on one axis inside a scroll container that is also the app's
// primary navigation. The overlapping 30 lines are not worth putting regression
// risk on the home editor, and three of the rules below exist only here.
//
// Two kinds of item drag, each only among its own kind: the apps ([data-nav-app-slug]) and the
// main sections ([data-core-nav]). The Apps link and the account cluster are fixed chrome, and
// an app cannot be dropped between Home and Tasks, nor a section among the apps.
//
// Desktop only for v1. On phones the app items live inside the "More" dropup,
// where a long-press fights the browser's own gestures, and those items are not
// marked draggable at all.
const APP = "[data-nav-app-slug]"
// The main sections (Home, Tasks ... Feed, You) drag too, but only among themselves: a section
// and an app never trade places, and a section dropped next to one under More folds under More.
const CORE = "a[data-core-nav]"
const ITEM = `${APP}, ${CORE}`

// Pointer distance from the container's edge that starts an auto-scroll, and
// how far each frame scrolls. HTML5 drag does NOT auto-scroll a scroll
// container, and both navs scroll once enough apps are in them (the rail
// overflow-y, the dock overflow-x) — without this you simply cannot drag to a
// position that is currently off-screen.
const EDGE = 48
const SCROLL_STEP = 12

export default class extends Controller {
    static values = { url: String, coreUrl: String }

    connect() {
        // Same gate as the hover labels: a coarse pointer gets no drag.
        if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return

        this._onDragOver = this._onDragOver.bind(this)
        this._onDrop = (e) => e.preventDefault()
        this.element.addEventListener("dragover", this._onDragOver)
        this.element.addEventListener("drop", this._onDrop)
        const draggable = this._items("app").concat(this._items("core"))
        draggable.filter((el) => this._url(el)).forEach((el) => this._enable(el))
    }

    disconnect() {
        this.element.removeEventListener("dragover", this._onDragOver)
        this.element.removeEventListener("drop", this._onDrop)
        this._stopScroll()
    }

    // The items of one kind, in DOM order: the apps, or the main sections.
    _items(group = "app") {
        return Array.from(this.element.querySelectorAll(group === "core" ? CORE : APP))
    }

    _group(el) {
        return el.matches(CORE) ? "core" : "app"
    }

    _url(el) {
        return this._group(el) === "core" ? this.coreUrlValue : this.urlValue
    }

    _enable(el) {
        el.setAttribute("draggable", "true")
        el.addEventListener("dragstart", (e) => this._onDragStart(e, el))
        el.addEventListener("dragend", () => this._onDragEnd())
        // Every fresh interaction starts with a mousedown, so this is what
        // guarantees the guard below can only ever eat the click belonging to
        // the drag that armed it. See _onClick.
        el.addEventListener("mousedown", () => { this._suppressClick = false })
        // Capture phase: a click fired after a drag has to be swallowed BEFORE
        // Turbo's own document listener sees it, or the drag navigates.
        el.addEventListener("click", (e) => this._onClick(e), true)
        el.addEventListener("keydown", (e) => this._onKeyDown(e, el))
    }

    // ---- Drag ----

    // A nav item is an <a href>, and browsers make links draggable by default —
    // dragging one already starts a native link-drag today. So this is not
    // "adding" a drag, it is taking one over: our own setData replaces the URL
    // payload the browser would otherwise put on the clipboard.
    _onDragStart(e, el) {
        this._dragging = el
        this._moved = false
        el.classList.add("is-nav-dragging")
        e.dataTransfer.effectAllowed = "move"
        try {
            e.dataTransfer.setData("text/plain", el.dataset.navAppSlug || el.dataset.coreNav)
        } catch { /* older browsers reject setData outside a user gesture */ }
    }

    _onDragOver(e) {
        if (!this._dragging) return

        this._autoScroll(e.clientX, e.clientY)

        const over = e.target.closest ? e.target.closest(ITEM) : null
        // Only app items are drop positions. Anything else (fixed chrome, the
        // divider, the account cluster) is left un-prevented, so the browser
        // shows "no drop" there and the order cannot be broken open.
        if (!over || over === this._dragging || !this.element.contains(over)) return
        if (this._group(over) !== this._group(this._dragging)) return

        e.preventDefault()
        this._over = over
        const before = this._isBefore(over, e.clientX, e.clientY)
        over.parentElement.insertBefore(this._dragging, before ? over : over.nextSibling)
        this._moved = true
    }

    _onDragEnd() {
        this._stopScroll()
        const moved = this._dragging
        if (moved) moved.classList.remove("is-nav-dragging")
        this._dragging = null
        if (this._moved) {
            this._suppressClick = true
            this._persist(moved, this._over)
        }
        this._moved = false
        this._over = null
    }

    // A drag that ends on the item it started from can still be followed by a
    // click in some browsers, which would navigate away from the page the member
    // was just rearranging.
    //
    // The flag it reads is cleared on mousedown, NOT only when a click consumes
    // it — that difference is the whole correctness of this guard. Chrome fires
    // no click after an HTML5 drag, so a flag cleared only by a click stays
    // armed after every drag and swallows the member's next genuine click on
    // that item, which reads as a nav link that has simply stopped working.
    // Clearing on mousedown scopes the guard to the interaction that armed it.
    _onClick(e) {
        if (!this._suppressClick) return
        this._suppressClick = false
        e.preventDefault()
        e.stopPropagation()
    }

    // The nav is a column in the rail and a row in the dock, and the midpoint
    // test has to follow it — pin_reorder's OR of both axes reads the vertical
    // half in a horizontal row, which puts the drop on the wrong side. Measured
    // from the container, the same way rail_tip and account_menu decide where a
    // label goes, so all three agree about which nav this is.
    _isBefore(el, x, y) {
        const rect = el.getBoundingClientRect()
        return this._horizontal()
            ? (x - rect.left) < rect.width / 2
            : (y - rect.top) < rect.height / 2
    }

    _horizontal() {
        const rect = this.element.getBoundingClientRect()
        return rect.width > rect.height
    }

    // ---- Auto-scroll while dragging ----

    _autoScroll(x, y) {
        const rect = this.element.getBoundingClientRect()
        const horizontal = this._horizontal()
        const pos = horizontal ? x : y
        const start = horizontal ? rect.left : rect.top
        const end = horizontal ? rect.right : rect.bottom

        let delta = 0
        if (pos - start < EDGE) delta = -SCROLL_STEP
        else if (end - pos < EDGE) delta = SCROLL_STEP

        if (delta === 0) return this._stopScroll()
        if (this._scrollDelta === delta) return

        this._scrollDelta = delta
        this._stopScroll(true)
        const tick = () => {
            if (!this._dragging || !this._scrollDelta) return
            if (horizontal) this.element.scrollLeft += this._scrollDelta
            else this.element.scrollTop += this._scrollDelta
            this._scrollFrame = requestAnimationFrame(tick)
        }
        this._scrollFrame = requestAnimationFrame(tick)
    }

    _stopScroll(keepDelta = false) {
        if (this._scrollFrame) cancelAnimationFrame(this._scrollFrame)
        this._scrollFrame = null
        if (!keepDelta) this._scrollDelta = 0
    }

    // ---- Keyboard ----

    // Drag is a mouse-only affordance, which would put reordering out of reach
    // for anyone navigating by keyboard. Alt + arrow moves the focused app along
    // the nav's own axis and keeps focus on it, so a member can walk an app to
    // where they want it. Alt, not bare arrows: bare arrows must keep scrolling
    // the page while a nav link has focus.
    _onKeyDown(e, el) {
        if (!e.altKey || e.ctrlKey || e.metaKey) return

        const horizontal = this._horizontal()
        const back = horizontal ? "ArrowLeft" : "ArrowUp"
        const forward = horizontal ? "ArrowRight" : "ArrowDown"
        if (e.key !== back && e.key !== forward) return

        if (!this._url(el)) return
        const items = this._items(this._group(el))
        const index = items.indexOf(el)
        const target = index + (e.key === forward ? 1 : -1)
        if (index < 0 || target < 0 || target >= items.length) return

        e.preventDefault()
        const neighbour = items[target]
        neighbour.parentElement.insertBefore(
            el,
            e.key === forward ? neighbour.nextSibling : neighbour
        )
        el.focus()
        this._persist(el, neighbour)
    }

    // ---- Persistence ----

    // `moved` is the item the member just placed. A main section also takes the placement of the
    // item it landed beside (shown, or under More), so dragging one across the fold moves it.
    _persist(moved, over) {
        const core = moved && this._group(moved) === "core"
        const body = core ? this._corePayload(moved, over) : { slugs: this._items("app").map((el) => el.dataset.navAppSlug).filter(Boolean) }
        fetch(core ? this.coreUrlValue : this.urlValue, {
            method: "PATCH",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                "X-CSRF-Token": csrfToken()
            },
            credentials: "same-origin",
            body: JSON.stringify(body)
        }).catch(() => {
            // The DOM already shows the new order and the next navigation will
            // re-render from the server, so a failed write self-corrects. Not
            // worth a toast over the nav.
        })
    }

    _corePayload(moved, over) {
        const placement = (over && over.dataset.corePlacement) || moved.dataset.corePlacement
        moved.dataset.corePlacement = placement
        moved.classList.toggle("is-core-nav-extra", placement === "more" && !moved.classList.contains("is-active"))
        return { order: this._items("core").map((el) => el.dataset.coreNav), moved: moved.dataset.coreNav, placement }
    }
}
